import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { trackLead } from "@/utils/metaPixel";
import { 
  Heart, 
  Instagram,
  Clock,
  CheckCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WaitlistPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error("Bitte gib deinen Namen ein");
      return;
    }
    
    if (!email.trim()) {
      toast.error("Bitte gib deine E-Mail-Adresse ein");
      return;
    }
    
    if (!consent) {
      toast.error("Bitte stimme der Datenschutzerklärung zu");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim(),
          consent: consent,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        trackLead();
        setIsSuccess(true);
      } else {
        toast.error(data.detail || data.message || "Ein Fehler ist aufgetreten");
      }
    } catch (error) {
      console.error("Waitlist signup error:", error);
      toast.error("Verbindungsfehler. Bitte versuche es erneut.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] font-body flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-[#8B2332] flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="font-heading text-3xl text-[#2D2D2D] mb-4">
            Du bist auf der Warteliste!
          </h1>
          
          <p className="text-[#6B6B6B] mb-8">
            Wir melden uns, sobald der nächste Pop-up Store ansteht. 
            Dann bist du automatisch bei der Expresskasse dabei.
          </p>
          
          <a
            href="https://www.instagram.com/edytandbloom/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#8B2332] text-white rounded-full font-medium hover:bg-[#7A1E2B] transition-colors"
          >
            <Instagram className="w-4 h-4" />
            Folge @edytandbloom
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-body">
      <div className="min-h-screen flex flex-col justify-center px-4 py-12">
        <div className="max-w-md mx-auto w-full">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-6">
              <Heart className="w-8 h-8 text-[#8B2332]" />
              <span className="font-heading text-2xl text-[#2D2D2D]">EDYT & BLOOM</span>
            </div>
          </div>

          {/* Sold Out Message */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#EFEBE5] text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#8B2332]/10 flex items-center justify-center mx-auto mb-6">
              <Clock className="w-8 h-8 text-[#8B2332]" />
            </div>
            
            <h1 className="font-heading text-2xl md:text-3xl text-[#2D2D2D] mb-4">
              Die Expresskasse ist leider ausgebucht.
            </h1>
            
            <p className="text-[#6B6B6B] text-lg">
              Komm gerne spontan vorbei und nutze die normale Kasse.
            </p>
          </div>

          {/* Waitlist Form */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#EFEBE5]">
            <p className="text-[#6B6B6B] text-sm text-center mb-6">
              Du kannst dich auch schon hier anmelden für die Warteliste für den nächsten Pop-up Store. 
              Dann bist du nächstes Mal automatisch bei der Expresskasse dabei.
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="text"
                  placeholder="Dein Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-12 bg-[#FAF7F2] border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332] rounded-lg"
                  data-testid="waitlist-input-name"
                  disabled={isLoading}
                />
              </div>
              
              <div>
                <Input
                  type="email"
                  placeholder="Deine E-Mail-Adresse"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 bg-[#FAF7F2] border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332] rounded-lg"
                  data-testid="waitlist-input-email"
                  disabled={isLoading}
                />
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  checked={consent}
                  onCheckedChange={setConsent}
                  className="mt-1 border-[#8B2332] data-[state=checked]:bg-[#8B2332] data-[state=checked]:border-[#8B2332]"
                  data-testid="waitlist-checkbox-consent"
                  disabled={isLoading}
                />
                <label 
                  htmlFor="consent" 
                  className="text-sm text-[#6B6B6B] leading-relaxed cursor-pointer"
                >
                  Ich möchte Infos zum nächsten Pop-up Store per E-Mail erhalten. Abmeldung jederzeit möglich.
                </label>
              </div>

              <Button
                type="submit"
                className="w-full h-14 bg-[#8B2332] hover:bg-[#7A1E2B] text-white text-lg font-medium rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                data-testid="waitlist-submit-button"
                disabled={isLoading}
              >
                {isLoading ? "Wird angemeldet..." : "Auf Warteliste setzen"}
              </Button>
            </form>
          </div>

          {/* Footer Info */}
          <div className="text-center mt-8">
            <p className="text-[#6B6B6B] text-sm">
              Hijab & Halal Pop-up Store • Hamburg
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 bg-white border-t border-[#EFEBE5]">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 text-[#6B6B6B]">
              <Heart className="w-4 h-4 text-[#8B2332]" />
              <span className="font-medium text-[#2D2D2D]">EDYT & BLOOM</span>
            </div>
            
            <div className="flex items-center gap-6 text-sm">
              <Link 
                to="/impressum" 
                className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors"
              >
                Impressum
              </Link>
              <Link 
                to="/datenschutz" 
                className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors"
              >
                Datenschutz
              </Link>
              <a 
                href="https://www.instagram.com/edytandbloom/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WaitlistPage;
